import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, filter, take, finalize } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { KeySystemNavConfig } from '../../../configuration/key.system.nav.config';
import { KeyingStructureService } from '../../../services/keying.structure.service';
import { KeyingSystemService } from '../../../services/keying.system.service';
import { KeyingBookService } from '../../../services/keying.book.service';
import { ErrorService } from '../../../services/error.service';
import { KeySystem, Structure, BittingSearchResult } from '../../../models/dev.test.models';
import * as moment from 'moment';

@Component({
  selector: 'app-tools',
  templateUrl: './tools.component.html',
  styleUrls: ['./tools.component.scss'],
  standalone: false
})
export class ToolsComponent implements OnInit, OnDestroy {
  structureId?: number;
  keySystemId?: number;
  structure: Structure;
  keySystem: KeySystem;

  // Find Bitting properties
  bittingSearchInput = '';
  bittingSearchResults: MatTableDataSource<BittingSearchResult> | null = null;
  isSearching = false;
  displayedColumns: string[] = ['keySetSymbol', 'bookNumber', 'keySection', 'bitting'];

  // Export Data properties
  stateCode = '';
  includeDealerSpec = false;
  exportMessage = '';

  // Collapsible section states
  isFindBittingExpanded = false;
  isExportDataExpanded = false;

  private destroy$ = new Subject<void>();

  constructor(
    private keySystemNavConfig: KeySystemNavConfig,
    private keyingStructureService: KeyingStructureService,
    private keyingSystemService: KeyingSystemService,
    private keyingBookService: KeyingBookService,
    private errorService: ErrorService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Load structure and key system data to populate navigation
    combineLatest([
      this.keyingStructureService.structure$.pipe(filter((x) => !!x)),
      this.keyingSystemService.keySystem$.pipe(filter((x) => !!x))
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([structure, keySystem]: [Structure, KeySystem]) => {
        this.structure = structure;
        this.keySystem = keySystem;
        this.structureId = structure.id;
        this.keySystemId = keySystem.id;
        // Set navigation with 'Quick Actions' as active page
        this.keySystemNavConfig.setNav('Quick Actions', this.structure, this.keySystem);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchBitting(): void {
    if (!this.bittingSearchInput || !this.bittingSearchInput.trim()) {
      return;
    }

    if (!this.keySystem?.masterKeysKeying?.id) {
      this.errorService.throw({ message: 'Top Master Key ID not found' });
      return;
    }

    this.isSearching = true;
    this.bittingSearchResults = null;

    const topMasterKeyId = this.keySystem.masterKeysKeying.id;

    this.keyingBookService
      .findBitting(this.keySystemId!, topMasterKeyId, this.bittingSearchInput.trim())
      .pipe(
        take(1),
        finalize(() => {
          this.isSearching = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (results) => {
          this.bittingSearchResults = new MatTableDataSource(results);
          this.cdr.detectChanges();
        },
        (error) => {
          this.errorService.throw(error);
        }
      );
  }

  clearBittingSearch(): void {
    this.bittingSearchInput = '';
    this.bittingSearchResults = null;
  }

  exportData(): void {
    if (!this.stateCode || !this.stateCode.trim()) {
      this.errorService.throw({ message: 'State code is required' });
      return;
    }

    this.exportMessage = '';

    this.keyingStructureService
      .exportStructuresCSV(this.stateCode.trim(), this.includeDealerSpec)
      .pipe(take(1))
      .subscribe(
        (res) => {
          // Create a new blob object with mime-type explicitly set
          const newBlob = new Blob([res], { type: 'text/csv' });

          // Create download link
          const data = window.URL.createObjectURL(newBlob);
          const link = document.createElement('a');
          link.href = data;

          // Construct filename with uppercase state codes
          const stateCodeList = this.stateCode
            .split(',')
            .map((s) => s.trim().toUpperCase())
            .join('_');
          link.download = `Structures_${stateCodeList}_${moment().format('YYYYMMDDHHmmss')}.csv`;

          link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));

          setTimeout(() => {
            window.URL.revokeObjectURL(data);
          }, 100);

          const dealerSpecText = this.includeDealerSpec ? ' (including dealer spec)' : '';
          this.exportMessage = `Structure data exported successfully as CSV for state code: ${this.stateCode.trim()}${dealerSpecText}`;

          // Clear message after 5 seconds
          setTimeout(() => {
            this.exportMessage = '';
          }, 5000);
        },
        (error) => {
          this.errorService.throw(error);
        }
      );
  }

  clearExport(): void {
    this.stateCode = '';
    this.includeDealerSpec = false;
    this.exportMessage = '';
  }

  toggleFindBittingSection(): void {
    this.isFindBittingExpanded = !this.isFindBittingExpanded;
  }

  toggleExportDataSection(): void {
    this.isExportDataExpanded = !this.isExportDataExpanded;
  }

  // Summary text methods for collapsed sections
  getFindBittingSummary(): string {
    if (this.bittingSearchResults && this.bittingSearchResults.data.length > 0) {
      return `(${this.bittingSearchResults.data.length} result${this.bittingSearchResults.data.length === 1 ? '' : 's'} found)`;
    }
    return '';
  }

  getExportDataSummary(): string {
    if (this.exportMessage) {
      return '(Export completed)';
    }
    return '';
  }
}
